/* DEV-2026-0220 — Händlerkarte
 *
 * Zwei Aufgaben, streng getrennt:
 *   1. Filtern der Liste (funktioniert immer, auch ohne Karte)
 *   2. Karte zeichnen (optional — schlägt sie fehl, bleibt die Liste nutzbar)
 *
 * Die Händlerdaten stehen schon im HTML (serverseitig gerendert). Es wird nichts
 * nachgeladen: kein API-Aufruf, keine Wartezeit, funktioniert ohne JavaScript
 * als reine Liste.
 *
 * Der gesamte Zustand liegt in `initialisiere` und damit pro Section. Vorher
 * stand er auf Modulebene und `querySelector` griff nur die erste Section:
 * zwei Händlerkarten auf einer Seite hätten sich Filter, Karte und Marker
 * geteilt. Derselbe Defekt war im Mockup-Teaser aufgefallen und ist hier
 * nachgezogen.
 */
(function () {
  'use strict';

  function initialisiere(wurzel) {
    if (wurzel.getAttribute('data-hk-init') === 'true') return;
    wurzel.setAttribute('data-hk-init', 'true');

    var eintraege = Array.prototype.slice.call(wurzel.querySelectorAll('[data-hk-eintrag]'));
    if (eintraege.length === 0) return;

    var suchfeld = wurzel.querySelector('[data-hk-suche]');
    var trefferAnzeige = wurzel.querySelector('[data-hk-treffer]');
    var keineTreffer = wurzel.querySelector('[data-hk-keine-treffer]');
    var karteEl = wurzel.querySelector('[data-hk-karte]');

    // ── Filter ────────────────────────────────────────────────────────────────

    /* Umlaute werden in ZWEI Formen verglichen, weil Nutzer beides tippen:
     *   "lübeck"  → lang "luebeck"
     *   "luebeck" → lang "luebeck"   (trifft)
     *   "lubeck"  → kurz "lubeck"    (trifft ueber die Kurzform)
     * Beim Test fiel auf, dass "luebeck" nichts fand, "lübeck" aber schon —
     * eine Suche, die an der Tastaturschreibweise scheitert, ist unbrauchbar. */
    function lang(text) {
      return String(text || '')
        .toLowerCase()
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]/g, '');
    }

    function kurz(text) {
      return lang(text).replace(/ae/g, 'a').replace(/oe/g, 'o').replace(/ue/g, 'u');
    }

    /** Sucht in PLZ, Ort und Name. PLZ zusätzlich als Präfix, damit "479" die
     *  ganze Region findet — bei einer Händlersuche will man die Umgebung sehen. */
    function passt(eintrag, suche) {
      if (!suche) return true;
      var plz = eintrag.dataset.plz || '';
      var suchePlz = suche.replace(/\D/g, '');
      if (suchePlz && plz.indexOf(suchePlz) === 0) return true;
      var quelle = (eintrag.dataset.ort || '') + ' ' + (eintrag.dataset.name || '');
      if (lang(quelle).indexOf(lang(suche)) !== -1) return true;
      return kurz(quelle).indexOf(kurz(suche)) !== -1;
    }

    var sichtbare = eintraege.slice();

    /* ── Umkreissuche (DEV-2026-0220, Nacharbeit) ───────────────────────────
     *
     * Gewuenscht: Treffer im Umkreis von 30 km; findet sich dort keiner,
     * stattdessen die drei naechstgelegenen. Dafuer braucht die Eingabe
     * Koordinaten — ein reiner Textvergleich kennt keine Entfernung.
     *
     * Die Koordinaten kommen aus zwei Quellen, in dieser Reihenfolge:
     *   1. aus den Haendlern selbst, wenn die eingegebene PLZ oder der Ort zu
     *      einem von ihnen passt — kostet keinen Netzaufruf und wirkt sofort
     *   2. sonst per Nominatim, eine Anfrage pro Suchbegriff (nicht pro
     *      Tastendruck), Ergebnis wird zwischengespeichert
     *
     * Schlaegt beides fehl, bleibt es beim bisherigen Textfilter. Die Suche
     * funktioniert also immer, dann eben ohne Entfernungen. */
    var UMKREIS_KM = parseInt(wurzel.dataset.umkreis || '30', 10) || 30;
    var NAECHSTE_WENN_LEER = parseInt(wurzel.dataset.naechste || '3', 10) || 3;
    var geoCache = {};

    function entfernungKm(lat1, lng1, lat2, lng2) {
      var R = 6371;
      var dLat = (lat2 - lat1) * Math.PI / 180;
      var dLng = (lng2 - lng1) * Math.PI / 180;
      var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    /** Koordinaten aus den vorhandenen Haendlern, wenn die Eingabe zu einem passt. */
    function koordinatenAusListe(suche) {
      var suchePlz = suche.replace(/\D/g, '');
      var kandidaten = eintraege.filter(function (e) {
        if (!e.dataset.lat || !e.dataset.lng) return false;
        if (suchePlz.length >= 3) return (e.dataset.plz || '').indexOf(suchePlz) === 0;
        if (suchePlz) return false;
        return lang(e.dataset.ort || '') === lang(suche);
      });
      if (!kandidaten.length) return null;
      // Mittelpunkt der Treffer — bei einem Ort mit mehreren Haendlern
      // sinnvoller als der erstbeste.
      var sLat = 0, sLng = 0;
      kandidaten.forEach(function (e) { sLat += parseFloat(e.dataset.lat); sLng += parseFloat(e.dataset.lng); });
      return { lat: sLat / kandidaten.length, lng: sLng / kandidaten.length };
    }

    /** Nominatim — nur wenn die Liste nichts hergibt. Fehler sind unkritisch. */
    function koordinatenGeocodiert(suche) {
      var schluessel = suche.toLowerCase();
      if (Object.prototype.hasOwnProperty.call(geoCache, schluessel)) {
        return Promise.resolve(geoCache[schluessel]);
      }
      var istPlz = /^\d{4,5}$/.test(suche.replace(/\s/g, ''));
      var url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=de,at&' +
        (istPlz ? 'postalcode=' + encodeURIComponent(suche) : 'city=' + encodeURIComponent(suche));
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : []; })
        .then(function (j) {
          var t = (Array.isArray(j) && j[0]) ? { lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) } : null;
          geoCache[schluessel] = t;
          return t;
        })
        .catch(function () { geoCache[schluessel] = null; return null; });
    }

    function abstandAnzeigen(el, km) {
      var vorhanden = el.querySelector('[data-hk-abstand]');
      if (km === null) { if (vorhanden) vorhanden.remove(); return; }
      var text = (km < 10 ? km.toFixed(1) : String(Math.round(km))) + ' km entfernt';
      if (vorhanden) { vorhanden.textContent = text; return; }
      var span = document.createElement('span');
      span.className = 'hk__eintrag-abstand';
      span.setAttribute('data-hk-abstand', '');
      span.textContent = text;
      var ziel = el.querySelector('.hk__eintrag-adresse') || el.querySelector('.hk__eintrag-knopf');
      if (ziel) ziel.appendChild(span);
    }

    /** Sichtbarkeit und Entfernungsangabe anhand eines Mittelpunkts setzen. */
    function nachEntfernungFiltern(mitte) {
      var mitAbstand = [];
      eintraege.forEach(function (e) {
        var la = parseFloat(e.dataset.lat), lo = parseFloat(e.dataset.lng);
        if (!isFinite(la) || !isFinite(lo)) { e.hidden = true; abstandAnzeigen(e, null); return; }
        mitAbstand.push({ el: e, km: entfernungKm(mitte.lat, mitte.lng, la, lo) });
      });
      mitAbstand.sort(function (a, b) { return a.km - b.km; });

      var imUmkreis = mitAbstand.filter(function (x) { return x.km <= UMKREIS_KM; });
      // Keiner im Umkreis? Dann die naechstgelegenen zeigen statt gar nichts.
      var ausgewaehlt = imUmkreis.length ? imUmkreis : mitAbstand.slice(0, NAECHSTE_WENN_LEER);
      var gewaehlt = ausgewaehlt.map(function (x) { return x.el; });

      eintraege.forEach(function (e) { e.hidden = gewaehlt.indexOf(e) === -1; });
      // Liste nach Entfernung sortieren — der naechste zuerst.
      var liste = wurzel.querySelector('[data-hk-liste]');
      if (liste) ausgewaehlt.forEach(function (x) { liste.appendChild(x.el); });
      ausgewaehlt.forEach(function (x) { abstandAnzeigen(x.el, x.km); });
      mitAbstand.forEach(function (x) { if (gewaehlt.indexOf(x.el) === -1) abstandAnzeigen(x.el, null); });

      sichtbare = gewaehlt;
      return { imUmkreis: imUmkreis.length, gezeigt: ausgewaehlt.length };
    }

    /** Reine Textsuche — Rueckfallebene ohne Koordinaten. */
    function textFiltern(suche) {
      sichtbare = [];
      eintraege.forEach(function (e) {
        var zeigen = passt(e, suche);
        e.hidden = !zeigen;
        abstandAnzeigen(e, null);
        if (zeigen) sichtbare.push(e);
      });
    }

    function anzeigeAktualisieren(hinweis) {
      if (trefferAnzeige) {
        trefferAnzeige.textContent = sichtbare.length + ' Händler' + (hinweis ? ' — ' + hinweis : '');
      }
      if (keineTreffer) keineTreffer.hidden = sichtbare.length > 0;
      if (karte) markerAktualisieren();
    }

    function umkreisHinweis(r) {
      return r.imUmkreis
        ? 'im Umkreis von ' + UMKREIS_KM + ' km'
        : 'keiner im Umkreis von ' + UMKREIS_KM + ' km, daher die ' + r.gezeigt + ' nächstgelegenen';
    }

    function filtern() {
      var suche = suchfeld ? suchfeld.value.trim() : '';

      if (!suche) {
        eintraege.forEach(function (e) { e.hidden = false; abstandAnzeigen(e, null); });
        sichtbare = eintraege.slice();
        anzeigeAktualisieren('');
        return;
      }

      // 1) Koordinaten aus der Liste — ohne Netzaufruf
      var mitte = koordinatenAusListe(suche);
      if (mitte) {
        anzeigeAktualisieren(umkreisHinweis(nachEntfernungFiltern(mitte)));
        return;
      }

      // 2) Sofort die Textsuche zeigen, damit die Eingabe nicht haengt …
      textFiltern(suche);
      anzeigeAktualisieren('');

      // … und parallel geocodieren. Eine verspaetete Antwort wird nur
      // uebernommen, wenn im Feld noch derselbe Begriff steht.
      koordinatenGeocodiert(suche).then(function (treffer) {
        if (!treffer) return;
        if ((suchfeld ? suchfeld.value.trim() : '') !== suche) return;
        anzeigeAktualisieren(umkreisHinweis(nachEntfernungFiltern(treffer)));
      });
    }

    if (suchfeld) {
      var timer = null;
      suchfeld.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(filtern, 150);
      });
      // Enter soll nicht die Seite neu laden (input type=search in einem Formular)
      suchfeld.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') e.preventDefault();
      });
    }

    // ── Karte ─────────────────────────────────────────────────────────────────

    var karte = null;
    var marker = [];
    var anbieter = wurzel.dataset.anbieter || 'osm';
    var startZoom = parseInt(wurzel.dataset.zoom || '6', 10);

    function mitKoordinaten(liste) {
      return liste.filter(function (e) { return e.dataset.lat && e.dataset.lng; });
    }

    function skriptLaden(url, integrity) {
      return new Promise(function (aufloesen, ablehnen) {
        var s = document.createElement('script');
        s.src = url;
        s.async = true;
        if (integrity) { s.integrity = integrity; s.crossOrigin = 'anonymous'; }
        s.onload = aufloesen;
        s.onerror = function () { ablehnen(new Error('Skript nicht ladbar: ' + url)); };
        document.head.appendChild(s);
      });
    }

    function stilLaden(url, integrity) {
      return new Promise(function (aufloesen) {
        var l = document.createElement('link');
        l.rel = 'stylesheet';
        l.href = url;
        if (integrity) { l.integrity = integrity; l.crossOrigin = 'anonymous'; }
        l.onload = aufloesen;
        l.onerror = aufloesen; // Stil ist nicht kritisch
        document.head.appendChild(l);
      });
    }

    /** Leaflet + OpenStreetMap — ohne Schlüssel und ohne Abrechnung. */
    function karteOsmAufbauen() {
      var basis = 'https://unpkg.com/leaflet@1.9.4/dist/';
      return stilLaden(basis + 'leaflet.css')
        .then(function () { return skriptLaden(basis + 'leaflet.js'); })
        .then(function () {
          var laedt = karteEl.querySelector('[data-hk-laedt]');
          if (laedt) laedt.remove();
          karte = L.map(karteEl, { scrollWheelZoom: false }).setView([51.3, 10.2], startZoom);
          L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 18,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          }).addTo(karte);
          markerAktualisieren();
        });
    }

    function markerAktualisieren() {
      if (!karte || typeof L === 'undefined') return;
      marker.forEach(function (m) { karte.removeLayer(m); });
      marker = [];
      var punkte = [];
      mitKoordinaten(sichtbare).forEach(function (e) {
        var lat = parseFloat(e.dataset.lat);
        var lng = parseFloat(e.dataset.lng);
        if (!isFinite(lat) || !isFinite(lng)) return;
        var m = L.marker([lat, lng]).addTo(karte);
        m.bindPopup(
          '<strong>' + e.dataset.name + '</strong><br>' +
          e.dataset.plz + ' ' + e.dataset.ort
        );
        m.on('click', function () { eintragHervorheben(e); });
        e.__marker = m;
        marker.push(m);
        punkte.push([lat, lng]);
      });
      if (punkte.length === 1) karte.setView(punkte[0], 12);
      else if (punkte.length > 1) karte.fitBounds(punkte, { padding: [30, 30] });
    }

    function eintragHervorheben(eintrag) {
      eintraege.forEach(function (e) { e.classList.remove('hk__eintrag--aktiv'); });
      eintrag.classList.add('hk__eintrag--aktiv');
      eintrag.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    // Klick in der Liste → Karte springt zum Standort
    wurzel.addEventListener('click', function (e) {
      var knopf = e.target.closest('[data-hk-fokus]');
      if (!knopf) return;
      var eintrag = knopf.closest('[data-hk-eintrag]');
      if (!eintrag) return;
      eintragHervorheben(eintrag);
      if (karte && eintrag.__marker) {
        karte.setView(eintrag.__marker.getLatLng(), 13);
        eintrag.__marker.openPopup();
      }
    });

    /* Karte erst laden, wenn sie in Sichtweite kommt. Auf dem Handy steht sie oben,
     * auf dem Desktop sofort sichtbar — aber wer die Seite nur überfliegt, lädt so
     * keine Kartenkacheln. */
    function karteStarten() {
      if (!karteEl || karte) return;
      var aufbau = anbieter === 'google' ? karteGoogleAufbauen() : karteOsmAufbauen();
      aufbau.catch(function (err) {
        // Die Liste bleibt vollständig nutzbar — deshalb nur ein Hinweis, kein Abbruch.
        console.warn('[haendlerkarte] Karte nicht verfügbar:', err.message);
        karteEl.classList.add('hk__karte--fehler');
        karteEl.textContent = 'Karte konnte nicht geladen werden. Die Händlerliste findest du daneben.';
      });
    }

    /** Google Maps — nur mit hinterlegtem Schlüssel. */
    function karteGoogleAufbauen() {
      var key = wurzel.dataset.googleKey;
      if (!key) return Promise.reject(new Error('Kein Google-Maps-Schlüssel hinterlegt'));
      return skriptLaden('https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key)).then(function () {
        var laedtG = karteEl.querySelector('[data-hk-laedt]');
        if (laedtG) laedtG.remove();
        var g = new google.maps.Map(karteEl, {
          center: { lat: 51.3, lng: 10.2 },
          zoom: startZoom,
          mapTypeControl: false,
          streetViewControl: false,
        });
        var grenzen = new google.maps.LatLngBounds();
        mitKoordinaten(eintraege).forEach(function (e) {
          var pos = { lat: parseFloat(e.dataset.lat), lng: parseFloat(e.dataset.lng) };
          var m = new google.maps.Marker({ position: pos, map: g, title: e.dataset.name });
          m.addListener('click', function () { eintragHervorheben(e); });
          grenzen.extend(pos);
        });
        g.fitBounds(grenzen);
      });
    }

    if ('IntersectionObserver' in window && karteEl) {
      var beobachter = new IntersectionObserver(function (eintraegeIO) {
        if (eintraegeIO.some(function (i) { return i.isIntersecting; })) {
          beobachter.disconnect();
          karteStarten();
        }
      }, { rootMargin: '200px' });
      beobachter.observe(karteEl);
    } else {
      karteStarten();
    }
  }

  var bereiche = document.querySelectorAll('[data-hk]');
  for (var n = 0; n < bereiche.length; n++) initialisiere(bereiche[n]);
})();
